<div class="app-main__inner">

  <div class="bb-settings-wrap">

    <!-- ── General Settings card ──────────────────────────────────────── -->
    <div class="card bb-settings-card mb-3">
      <div class="card-header fw-semibold d-flex align-items-center gap-2">
        <i class="pe-7s-config text-primary"></i>General Settings
      </div>
      <div class="card-body">
        <div class="row g-3 align-items-end" style="max-width:480px">
          <div class="col-8">
            <label class="form-label fw-semibold mb-1">
              Membership Deposit Amount (₹)
              <span class="text-muted fw-normal small ms-1">— shown as read-only when registering a new member</span>
            </label>
            <div class="input-group input-group-sm">
              <span class="input-group-text">₹</span>
              <input type="number" class="form-control" [(ngModel)]="depositAmount" min="0" step="1"
                     placeholder="0" [disabled]="depositAmountSaving" />
            </div>
            <div class="text-danger small mt-1" *ngIf="depositAmountError">{{ depositAmountError }}</div>
            <div class="text-success small mt-1" *ngIf="depositAmountSuccess">{{ depositAmountSuccess }}</div>
          </div>
          <div class="col-4">
            <button type="button" class="btn btn-primary btn-sm w-100"
                    (click)="saveDepositAmount()" [disabled]="depositAmountSaving || !isDepositDirty">
              <span *ngIf="depositAmountSaving" class="spinner-border spinner-border-sm me-1"></span>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Member Code Settings card ────────────────────────────────────── -->
    <div class="card bb-settings-card mb-3">
      <div class="card-header fw-semibold d-flex align-items-center gap-2">
        <i class="pe-7s-id text-primary"></i>Member Code (memNo) Settings
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">
          Enter letters/digits only — the <code>/</code> separator is added automatically.
          A prefix of <code>HD</code> produces codes like <code>HD/1</code>, <code>HD/2</code>, …
          Leave blank to use a plain serial number. Each prefix has its own independent counter.
        </p>
        <div class="row g-3 align-items-end" style="max-width:520px">
          <div class="col-6">
            <label class="form-label fw-semibold mb-1">
              Home Delivery Prefix
              <span class="text-muted fw-normal small ms-1">(member type H)</span>
            </label>
            <input type="text" class="form-control form-control-sm text-uppercase"
                   [(ngModel)]="homeDeliveryPrefix"
                   (ngModelChange)="homeDeliveryPrefix = $event.toUpperCase(); memberCodeError = ''"
                   placeholder="e.g. HD  (leave blank for none)"
                   maxlength="10" [disabled]="memberCodeSaving" />
          </div>
          <div class="col-6">
            <label class="form-label fw-semibold mb-1">
              Center Prefix
              <span class="text-muted fw-normal small ms-1">(member type A)</span>
            </label>
            <input type="text" class="form-control form-control-sm text-uppercase"
                   [(ngModel)]="centerPrefix"
                   (ngModelChange)="centerPrefix = $event.toUpperCase(); memberCodeError = ''"
                   placeholder="e.g. C  (leave blank for none)"
                   maxlength="10" [disabled]="memberCodeSaving" />
          </div>
          <div class="col-12 pt-0">
            <div class="text-danger small mb-1" *ngIf="memberCodeError">{{ memberCodeError }}</div>
            <div class="text-success small mb-1" *ngIf="memberCodeSuccess">
              <i class="pe-7s-check me-1"></i>{{ memberCodeSuccess }}
            </div>
            <button type="button" class="btn btn-primary btn-sm"
                    (click)="saveMemberCodeSettings()"
                    [disabled]="memberCodeSaving || !isMemberCodeDirty">
              <span *ngIf="memberCodeSaving" class="spinner-border spinner-border-sm me-1"></span>
              <i *ngIf="!memberCodeSaving" class="pe-7s-diskette me-1"></i>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Date Display Format card ─────────────────────────────────────── -->
    <div class="card bb-settings-card mb-3">
      <div class="card-header fw-semibold d-flex align-items-center gap-2">
        <i class="pe-7s-date text-primary"></i>Date Display Format
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">
          Choose how dates are shown throughout the application — in tables, forms, and reports.
        </p>
        <div class="d-flex flex-wrap gap-2 mb-3">
          <div *ngFor="let opt of dateFormatOptions"
               class="bb-date-fmt-option"
               [class.selected]="selectedDateFormat === opt.value"
               (click)="selectedDateFormat = opt.value; dateFormatError = ''">
            <span class="fmt-label">{{ opt.label }}</span>
            <span class="fmt-example text-muted small">{{ opt.example }}</span>
          </div>
        </div>
        <div class="text-danger small mb-2" *ngIf="dateFormatError">{{ dateFormatError }}</div>
        <div class="d-flex align-items-center gap-3">
          <button type="button" class="btn btn-primary btn-sm"
                  (click)="saveDateFormatSetting()"
                  [disabled]="dateFormatSaving || !isDateFormatDirty">
            <span *ngIf="dateFormatSaving" class="spinner-border spinner-border-sm me-1"></span>
            <i *ngIf="!dateFormatSaving" class="pe-7s-diskette me-1"></i>
            Save
          </button>
          <span *ngIf="dateFormatSuccess" class="text-success small">
            <i class="pe-7s-check me-1"></i>{{ dateFormatSuccess }}
          </span>
        </div>
      </div>
    </div>

    <!-- ── Access Code card ─────────────────────────────────────────────── -->
    <div class="card bb-settings-card mb-3" *ngIf="auth.getCurrentUser()?.role === 'admin'">
      <div class="card-header fw-semibold d-flex align-items-center gap-2">
        <i class="pe-7s-key text-primary"></i>Access Code
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">
          The access code is entered by all users at login to identify your library account.
          Maximum 10 characters, letters and digits only.
        </p>
        <div class="alert alert-warning py-2 px-3 small mb-3">
          <i class="pe-7s-attention me-1"></i>
          <strong>Important:</strong> After changing the access code, every team member must use the new code at their next login.
          Existing sessions are not affected.
        </div>
        <div class="mb-3" style="max-width:480px">
          <div class="d-flex align-items-center gap-2 mb-1">
            <span class="fw-semibold small text-muted text-uppercase" style="letter-spacing:.04em">Current Code</span>
            <code class="user-select-all fs-6 text-dark fw-bold" *ngIf="currentClientCode">{{ currentClientCode }}</code>
            <span class="text-muted small fst-italic" *ngIf="!currentClientCode">loading…</span>
          </div>
        </div>
        <div class="row g-3 align-items-end" style="max-width:480px">
          <div class="col-8">
            <label class="form-label fw-semibold mb-1">New Access Code</label>
            <input type="text" class="form-control form-control-sm"
                   [(ngModel)]="newClientCode"
                   (ngModelChange)="accessCodeError = ''"
                   placeholder="Up to 10 letters/digits"
                   maxlength="10" [disabled]="accessCodeSaving" />
            <div class="text-danger small mt-1" *ngIf="accessCodeError">{{ accessCodeError }}</div>
            <div class="text-success small mt-1" *ngIf="accessCodeSuccess">
              <i class="pe-7s-check me-1"></i>{{ accessCodeSuccess }}
            </div>
          </div>
          <div class="col-4">
            <button type="button" class="btn btn-primary btn-sm w-100"
                    (click)="saveAccessCode()"
                    [disabled]="accessCodeSaving || !isAccessCodeDirty">
              <span *ngIf="accessCodeSaving" class="spinner-border spinner-border-sm me-1"></span>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Label Name Source card ────────────────────────────────────────── -->
    <div class="card bb-settings-card mb-3">
      <div class="card-header fw-semibold d-flex align-items-center gap-2">
        <i class="pe-7s-print text-primary"></i>Library / Center Name on Labels
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">
          Choose what text appears as the library name on printed barcode labels.
        </p>
        <div class="d-flex flex-column gap-2 mb-2">
          <div class="form-check">
            <input class="form-check-input" type="radio" id="lns_center"
                   name="labelNameSource" [(ngModel)]="labelNameSource" value="center">
            <label class="form-check-label" for="lns_center">
              Center Name
              <span class="text-muted small ms-1">(from header center selection)</span>
            </label>
          </div>
          <div class="form-check">
            <input class="form-check-input" type="radio" id="lns_licensee"
                   name="labelNameSource" [(ngModel)]="labelNameSource" value="licensee">
            <label class="form-check-label" for="lns_licensee">
              Licensed To Name
              <span class="text-muted small ms-1">({{ auth.getCurrentUser()?.clientName || '—' }})</span>
            </label>
          </div>
          <div class="form-check">
            <input class="form-check-input" type="radio" id="lns_custom"
                   name="labelNameSource" [(ngModel)]="labelNameSource" value="custom">
            <label class="form-check-label" for="lns_custom">User Specified Text</label>
          </div>
        </div>
        <div *ngIf="labelNameSource === 'custom'" class="mb-2" style="max-width: 360px;">
          <input type="text" class="form-control form-control-sm"
                 [(ngModel)]="labelNameCustomText"
                 (ngModelChange)="nameSourceError = ''"
                 placeholder="e.g. Rasik Sahitya Library"
                 [class.is-invalid]="!!nameSourceError">
          <div class="invalid-feedback">{{ nameSourceError }}</div>
        </div>
        <div class="d-flex align-items-center gap-3">
          <button type="button" class="btn btn-primary btn-sm"
                  (click)="saveNameSource()" [disabled]="isSavingNameSource">
            <span *ngIf="isSavingNameSource" class="spinner-border spinner-border-sm me-1"></span>
            <i *ngIf="!isSavingNameSource" class="pe-7s-diskette me-1"></i>
            Save
          </button>
          <span *ngIf="nameSourceSuccess" class="text-success small">
            <i class="pe-7s-check me-1"></i>{{ nameSourceSuccess }}
          </span>
          <span *ngIf="nameSourceError && labelNameSource !== 'custom'" class="text-danger small">
            {{ nameSourceError }}
          </span>
        </div>
      </div>
    </div>

    <!-- ── Subscription Plans card ─────────────────────────────────────── -->
    <div class="card bb-settings-card">
      <div class="card-header d-flex align-items-center justify-content-between gap-2">
        <span class="fw-semibold d-flex align-items-center gap-2">
          <i class="pe-7s-ticket text-primary"></i>Subscription Plans
          <span *ngIf="isDirty && !isSaving" class="badge bg-warning-subtle text-warning border border-warning-subtle small">
            Unsaved changes
          </span>
        </span>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-secondary btn-sm" (click)="loadPlans()" [disabled]="isLoading || isSaving">
            <i class="pe-7s-refresh me-1"></i> Reload
          </button>
          <button *ngIf="isDirty || isSaving" type="button" class="btn btn-primary btn-sm"
                  (click)="saveChanges()" [disabled]="isSaving || plans.length === 0">
            <span *ngIf="isSaving" class="spinner-border spinner-border-sm me-1"></span>
            <i *ngIf="!isSaving" class="pe-7s-diskette me-1"></i>
            {{ isSaving ? 'Saving…' : 'Save Changes' }}
          </button>
        </div>
      </div>

      <div class="card-body">

        <p class="text-muted small mb-3">
          Configure membership durations and their charges. These values are used on the renewal screen in the mobile app.
        </p>

        <!-- Loading -->
        <div class="text-center py-4" *ngIf="isLoading">
          <div class="spinner-border text-primary spinner-border-sm"></div>
          <p class="mt-2 text-muted small">Loading plans…</p>
        </div>

        <ng-container *ngIf="!isLoading">

          <!-- Plan table -->
          <div class="table-responsive mb-3">
            <table class="table table-bordered align-middle bb-plan-table">
              <thead class="table-light">
                <tr>
                  <th class="col-months">Months</th>
                  <th class="col-label">Label</th>
                  <th class="col-type">Subscription Type</th>
                  <th class="col-charges">Charges (₹)</th>
                  <th class="col-action"></th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let plan of plans; let i = index">
                  <td>
                    <input type="number" class="form-control form-control-sm"
                           [(ngModel)]="plan.months" (ngModelChange)="onMonthsChange(plan)"
                           min="1" placeholder="e.g. 3" />
                  </td>
                  <td>
                    <input type="text" class="form-control form-control-sm"
                           [(ngModel)]="plan.label" placeholder="e.g. 3 Months" />
                  </td>
                  <td>
                    <input type="text" class="form-control form-control-sm"
                           [(ngModel)]="plan.subscriptionType" placeholder="e.g. Quarterly" />
                  </td>
                  <td>
                    <div class="input-group input-group-sm">
                      <span class="input-group-text">₹</span>
                      <input type="number" class="form-control"
                             [(ngModel)]="plan.charges" min="1" placeholder="e.g. 900" />
                    </div>
                  </td>
                  <td class="text-center">
                    <button type="button" class="btn btn-outline-danger btn-sm" (click)="removePlan(i)" title="Remove">
                      <i class="pe-7s-trash"></i>
                    </button>
                  </td>
                </tr>

                <tr *ngIf="plans.length === 0">
                  <td colspan="5" class="text-center text-muted py-3">
                    No plans configured. Click "Add Plan" to get started.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Add plan -->
          <button type="button" class="btn btn-outline-secondary btn-sm mb-3" (click)="addPlan()">
            <i class="pe-7s-plus me-1"></i> Add Plan
          </button>

          <!-- Feedback -->
          <div class="alert alert-danger py-2 px-3 small" *ngIf="errorMessage">
            <i class="pe-7s-attention me-1"></i>{{ errorMessage }}
          </div>
          <div class="alert alert-success py-2 px-3 small" *ngIf="successMessage">
            <i class="pe-7s-check me-1"></i>{{ successMessage }}
          </div>

        </ng-container>
      </div>
    </div>

  </div>
</div>
